import {
  Component,
  Input,
  HostBinding,
  ElementRef,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  OnDestroy,
} from "@angular/core";
import { DynamicComponentLoaderService } from "../../dynamic-component-loader/dynamic-component-loader.service";
import {
  isDefaultModel,
  defaultModel2UProperty as defaultModel2ViewModel,
} from "src/app/dynamic-module/lib/mapper/defaultModel2UProperty";
import { IUProperty } from "../../interface/umbraco-property";
import { PanelsService } from "../panels/panels.service";
import {
  IPanelSettings,
  resolveThemeCssClass,
} from "src/app/dynamic-module/lib/public_api";
import {
  IScopedCssController,
  ScopedPanelCssService,
} from "../../utils/scoped-panel-css.service";

export interface IPanel {
  contentTypeAlias: string;
  anchor: IUProperty<string>;
  panelSettings?: IPanelSettings;
  customCss?: IUProperty<string>;
  customCSS?: IUProperty<string>;
  panelBackground?: IUProperty<string>;
  hideOnMobile?: IUProperty<boolean>;
}

const PANEL_BACKGROUND_IMAGE_VAR = "--panel-background-image";

@Component({
  selector: "app-dynamic-component",
  templateUrl: "./dynamic.component.html",
  styleUrls: ["./dynamic.component.less"],
})
export class DynamicComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() data: IPanel | null = null;

  private scopedCssController: IScopedCssController | null = null;
  private scopedCssHostClass = "";
  private scopedCssPanelName = "";
  private viewInitialized = false;

  constructor(
    private dynamicComponentLoader: DynamicComponentLoaderService,
    private panelsService: PanelsService,
    private hostRef: ElementRef,
    private scopedPanelCssService: ScopedPanelCssService,
  ) {}

  rootClasses = "";

  @HostBinding("class")
  get hostClasses(): string {
    return [
      this.rootClasses,
      this.scopedCssHostClass,
      this.isHiddenOnMobile() ? "hide-on-mobile" : "",
    ]
      .filter(Boolean)
      .join(" ");
  }

  private isHiddenOnMobile(): boolean {
    return !!(this.data && this.data.hideOnMobile && this.data.hideOnMobile.value);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.data) {
      this.prepareData();
      this.syncScopedCss();
    }
  }

  ngAfterViewInit() {
    this.viewInitialized = true;
    this.syncScopedCss();
  }

  ngOnDestroy() {
    this.destroyScopedCss();
  }

  private prepareData() {
    if (!this.data || !this.data.contentTypeAlias) return;

    const dataMapper = this.dynamicComponentLoader.getDataMapperFor(
      this.data.contentTypeAlias,
    );
    if (isDefaultModel(this.data)) {
      this.data = dataMapper
        ? dataMapper.map(this.data)
        : defaultModel2ViewModel(this.data);
    }

    this.applyTheme();
    this.applyPanelBackground();
    this.panelsService.markAsLoaded({ data: this.data });
  }

  private syncScopedCss() {
    if (!this.data || !this.data.contentTypeAlias) {
      this.destroyScopedCss();
      return;
    }

    if (this.scopedCssPanelName !== this.data.contentTypeAlias) {
      this.destroyScopedCss();
      this.scopedCssController = this.scopedPanelCssService.create(
        this.data.contentTypeAlias,
      );
      this.scopedCssHostClass = this.scopedCssController.hostClass;
      this.scopedCssPanelName = this.data.contentTypeAlias;
    }

    if (!this.viewInitialized || !this.scopedCssController) {
      return;
    }

    this.scopedCssController.mount(this.hostRef.nativeElement);
    this.scopedCssController.update(this.getScopedCssValue());
  }

  private destroyScopedCss() {
    if (this.scopedCssController) {
      this.scopedCssController.destroy();
    }

    this.scopedCssController = null;
    this.scopedCssHostClass = "";
    this.scopedCssPanelName = "";
  }

  private getScopedCssValue(): string {
    if (this.data && this.data.customCss && this.data.customCss.value) {
      return this.data.customCss.value;
    }

    if (this.data && this.data.customCSS && this.data.customCSS.value) {
      return this.data.customCSS.value;
    }

    return "";
  }

  private applyTheme() {
    const panelSettings =
      this.data && this.data.panelSettings ? this.data.panelSettings : null;
    const theme =
      panelSettings && panelSettings.theme && panelSettings.theme.value;
    if (theme) {
      this.rootClasses = resolveThemeCssClass(panelSettings as IPanelSettings);
      const themeValue: any = theme;
      for (let key in theme) {
        if (theme.hasOwnProperty(key)) {
          this.hostRef.nativeElement.style.setProperty(
            this.toCSSVariableName(key),
            themeValue[key],
          );
        }
      }
    } else {
      this.rootClasses = "";
    }
  }

  // Exposes the "Background" setting to the .panel-background() LESS mixins as a
  // background-image, so it paints over the theme color. Plain colors are wrapped
  // into a solid gradient so one variable covers both cases.
  private applyPanelBackground() {
    const style: CSSStyleDeclaration = this.hostRef.nativeElement.style;
    style.removeProperty(PANEL_BACKGROUND_IMAGE_VAR);

    const rawValue =
      this.data && this.data.panelBackground && this.data.panelBackground.value;
    const value = (rawValue || "").trim().replace(/;+$/, "").trim();
    if (!value || /[{};]/.test(value)) return;

    const backgroundImage = /(gradient|url|image-set)\(/i.test(value)
      ? value
      : `linear-gradient(${value}, ${value})`;
    style.setProperty(PANEL_BACKGROUND_IMAGE_VAR, backgroundImage);
  }

  toCSSVariableName(str: string) {
    return (
      "--" +
      str
        .split("")
        .map((letter: string, idx: number) => {
          return letter.toUpperCase() === letter
            ? `${idx !== 0 ? "-" : ""}${letter.toLowerCase()}`
            : letter;
        })
        .join("")
    );
  }
}
